import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { MdArrowOutward } from "react-icons/md";
import SectionWrapper from "../components/SectionWrapper";
import RatingStars from "../components/ui/RatingStars";
import TestimonialCard from "../components/testimonials/TestimonialCard";
import TestimonialForm from "../components/testimonials/TestimonialForm";
import {
  addOrEditTestimonial,
  getAllTestimonials,
} from "../api/testimonialApi";
import { normalizeTestimonials } from "../utils/adminData";

const DEFAULT_AVATAR = "/ImageAndVideo/image/careimg5.jpg";

const TestimonialsPage = () => {
  const formSectionRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeFeaturedIndex, setActiveFeaturedIndex] = useState(0);
  const [toastVisible, setToastVisible] = useState(false);

  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);
  const [isSavingTestimonial, setIsSavingTestimonial] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    rating: 0,
    message: "",
    photoPreview: "",
  });

  const loadTestimonials = useCallback(
    async () => {
      const response = await getAllTestimonials();
      setTestimonials(normalizeTestimonials(response));
    },
    []
  );

  useEffect(() => {
    if (!location.state?.editTestimonial) return;

    const testimonial = location.state.editTestimonial;
    setFormData({
      fullName: testimonial.fullName || "",
      email: testimonial.email || "",
      company: testimonial.company || "",
      rating: testimonial.rating || 0,
      message: testimonial.message || "",
      photoPreview: testimonial.photo || "",
    });
    setEditingTestimonialId(testimonial.id);
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.state?.editTestimonial]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const approvedTestimonials = useMemo(
    () => testimonials.filter((item) => item.status === "approved"),
    [testimonials]
  );

  useEffect(() => {
    if (approvedTestimonials.length < 2) return undefined;
    const timer = setInterval(() => {
      setActiveFeaturedIndex((prev) => (prev + 1) % approvedTestimonials.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [approvedTestimonials.length]);

  useEffect(() => {
    if (!toastVisible) return undefined;
    const timer = setTimeout(() => setToastVisible(false), 3200);
    return () => clearTimeout(timer);
  }, [toastVisible]);

  const featuredTestimonial = approvedTestimonials.length
    ? approvedTestimonials[activeFeaturedIndex % approvedTestimonials.length]
    : null;

  const secondaryTestimonials = approvedTestimonials
    .filter((item) => item.id !== featuredTestimonial?.id)
    .slice(0, 4);

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setFormData((prev) => ({ ...prev, photoPreview: "" }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, photoPreview: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const isApiSuccess = (response) => response?.success !== false && response?.Success !== false;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSavingTestimonial(true);

    const payload = {
      testimonialId: editingTestimonialId || undefined,
      id: editingTestimonialId || undefined,
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      companyName: formData.company.trim(),
      rating: Number(formData.rating) || 0,
      review: formData.message.trim(),
      message: formData.message.trim(),
      imageUrl: formData.photoPreview || DEFAULT_AVATAR,
      photo: formData.photoPreview || DEFAULT_AVATAR,
      isApproved: false,
      status: "pending",
      approvalStatus: "pending",
    };

    const response = await addOrEditTestimonial(payload);

    if (isApiSuccess(response)) {
      setFormData({
        fullName: "",
        email: "",
        company: "",
        rating: 0,
        message: "",
        photoPreview: "",
      });
      setEditingTestimonialId(null);
      setToastVisible(true);
      await loadTestimonials();

      if (location.state?.returnTo) {
        navigate(location.state.returnTo, { replace: true });
      }
    } else {
      alert(response?.Message || "Unable to submit testimonial right now.");
    }

    setIsSavingTestimonial(false);
  };

  const handleScrollToForm = () => {
    if (!formSectionRef.current) return;
    formSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="mt-16 bg-cream pb-20">
      <SectionWrapper className="relative overflow-hidden pb-6 pt-10">
        <div className="absolute inset-0">
          <div className="absolute -left-20 top-4 h-44 w-44 rounded-full bg-maroon/20 blur-3xl" />
          <div className="absolute -right-16 bottom-2 h-40 w-40 rounded-full bg-sky-200/40 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(128,0,32,0.08),transparent_35%),radial-gradient(circle_at_80%_40%,rgba(30,64,175,0.1),transparent_35%)]" />
        </div>

        <div className="relative z-10 rounded-3xl border border-white/70 bg-white/60 p-7 shadow-[0_16px_60px_rgba(0,0,0,0.12)] backdrop-blur-xl md:p-10">
          <p className="inline-flex rounded-full bg-maroon/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-maroon">
            Trusted Experiences
          </p>
          <h1 className="mt-4 max-w-3xl text-3xl font-bold text-maroon md:text-5xl">
            Real Stories From Families We Care For
          </h1>
          <p className="mt-4 max-w-3xl text-base text-gray-700 md:text-lg">
            Every published testimonial is reviewed before it appears publicly. Explore approved reviews and share your own experience with Aegis Care.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleScrollToForm}
              className="inline-flex items-center gap-2 rounded-full bg-maroon px-6 py-3 text-sm font-semibold text-white transition hover:bg-maroon/90"
            >
              Share Your Experience
              <MdArrowOutward className="text-base" />
            </button>
            <span className="inline-flex items-center rounded-full border border-maroon/20 bg-white/80 px-4 py-3 text-sm font-semibold text-maroon">
              {approvedTestimonials.length} approved testimonials live
            </span>
          </div>
        </div>
      </SectionWrapper>

      <SectionWrapper className="pt-2">
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-2xl font-bold text-maroon md:text-3xl">Featured Testimonials</h2>
          <p className="text-sm text-gray-600">Only approved testimonials are displayed publicly.</p>
        </div>

        {featuredTestimonial ? (
          <div className="grid gap-5 lg:grid-cols-5">
            <motion.article
              key={featuredTestimonial.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/75 p-7 shadow-[0_14px_55px_rgba(0,0,0,0.11)] backdrop-blur-xl lg:col-span-3"
            >
              <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-maroon/10 blur-2xl" />
              <p className="inline-flex rounded-full bg-maroon/10 px-3 py-1 text-xs font-semibold text-maroon">
                Spotlight Story
              </p>
              <div className="mt-5 flex items-center gap-4">
                <img
                  src={featuredTestimonial.photo}
                  alt={featuredTestimonial.fullName}
                  className="h-16 w-16 rounded-full border border-maroon/20 object-cover"
                />
                <div>
                  <h3 className="text-xl font-semibold text-maroon">{featuredTestimonial.fullName}</h3>
                  <p className="text-sm text-gray-500">
                    {featuredTestimonial.company || "Individual Client"}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <RatingStars value={featuredTestimonial.rating} readonly size="text-lg" />
              </div>
              <p className="mt-4 text-base leading-relaxed text-gray-700">
                {featuredTestimonial.message}
              </p>
            </motion.article>

            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
              {secondaryTestimonials.map((item) => (
                <TestimonialCard key={item.id} item={item} mode="public" />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-maroon/30 bg-white/70 p-8 text-center text-sm text-gray-600 backdrop-blur">
            No approved testimonials yet. Submit feedback and approve from admin demo to publish.
          </div>
        )}
      </SectionWrapper>

    

      <SectionWrapper className="pt-0">
        <div ref={formSectionRef} className="grid gap-6 lg:grid-cols-5">
          <div className="rounded-3xl border border-white/70 bg-white/75 p-6 shadow-md backdrop-blur-xl lg:col-span-3 md:p-8">
            <h2 className="text-2xl font-bold text-maroon">Submit Your Feedback</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your testimonial will be marked as pending and published only after review.
            </p>

            <TestimonialForm
              formData={formData}
              onFieldChange={handleFormFieldChange}
              onPhotoChange={handlePhotoChange}
              onSubmit={handleSubmit}
              submitLabel={editingTestimonialId ? "Save Changes" : "Submit Feedback"}
              isSubmitting={isSavingTestimonial}
              submitDisabled={!formData.rating}
              showCaptcha
            />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-3xl border border-white/70 bg-white/75 p-6 shadow-md backdrop-blur-xl">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-maroon/70">
                Review Process
              </p>
              <p className="mt-3 text-4xl font-bold text-maroon">100%</p>
              <p className="mt-1 text-sm text-gray-600">
                Public pages display approved testimonials only.
              </p>
            </div>

            <div className="rounded-3xl border border-white/70 bg-white/75 p-6 shadow-md backdrop-blur-xl">
              <h3 className="text-lg font-semibold text-maroon">Preview</h3>
              <div className="mt-4 rounded-2xl border border-gray-200 bg-cream p-4">
                <img
                  src={formData.photoPreview || DEFAULT_AVATAR}
                  alt="Preview avatar"
                  className="h-14 w-14 rounded-full object-cover"
                />
                <p className="mt-3 text-sm font-semibold text-maroon">
                  {formData.fullName || "Your Name"}
                </p>
                <p className="text-xs text-gray-500">
                  {formData.company || "Company name"}
                </p>
                <div className="mt-2">
                  <RatingStars value={formData.rating} readonly />
                </div>
                <p className="mt-3 text-sm text-gray-700">
                {formData.message || "Your testimonial preview will appear here as you type."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>

      <AnimatePresence>
        {toastVisible ? (
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 22 }}
            className="fixed bottom-5 left-1/2 z-[90] w-[92%] max-w-xl -translate-x-1/2 rounded-xl bg-maroon px-5 py-4 text-sm text-white shadow-xl"
          >
            Thank you for your feedback. Our team will review it before publishing.
          </motion.div>
        ) : null}
      </AnimatePresence>
    </main>
  );
};

export default TestimonialsPage;
